/* eslint-disable no-unused-vars */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Card, Spin, message, Alert } from "antd";
import { DynamicTable } from "dynamic-table-builder";
import dayjs from "dayjs";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import API from "../../api";
import urls from "../../api/urls";
import Heading from "../../components/Heading";
import "./style.scss";

const { GET_PRS_STATUS_DATA } = urls;

// Default date range fallback
const getDefaultDateRange = () => [
  dayjs().startOf("day"),
  dayjs().endOf("day"),
];

// Session storage key - same as used in PRSAnalytics index.jsx
const SESSION_STORAGE_KEY = "prs_analytics_state";

// Helper function to get saved date range from session storage as fallback
const getSavedStateFromSession = () => {
  try {
    const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!saved) return null;

    const state = JSON.parse(saved);
    
    // Convert date strings back to moment objects if needed
    if (state.dateRange && state.dateRange.length === 2) {
      // Convert ISO strings back to dayjs objects
      state.dateRange = [
        dayjs(state.dateRange[0]),
        dayjs(state.dateRange[1]),
      ];
    }
    return state;
  } catch (error) {
    console.error("Error reading saved state from session:", error);
    return null;
  }
};

// Convert moment objects to dayjs if needed
const convertToDateRange = (range) => {
  if (!range || !Array.isArray(range) || range.length !== 2) {
    return getDefaultDateRange();
  }

  try {
    let start, end;

    // Handle moment objects (from PRSAnalytics)
    if (
      range[0]._isAMomentObject ||
      (range[0].format && range[0].toISOString)
    ) {
      start = dayjs(range[0].toISOString());
      end = dayjs(range[1].toISOString());
    }
    // Handle dayjs objects
    else if (range[0].$isDayjs) {
      start = range[0];
      end = range[1];
    }
    // Handle date strings or other formats
    else {
      start = dayjs(range[0]);
      end = dayjs(range[1]);
    }

    // Ensure the dates are valid
    if (!start.isValid() || !end.isValid()) {
      console.warn("Invalid dates in range, using default:", range);
      return getDefaultDateRange();
    }

    return [start, end];
  } catch (error) {
    console.error("Error converting date range:", error);
    return getDefaultDateRange();
  }
};

const PRSStatusDetails = () => {
  const { status } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get state from navigation or session storage as fallback - memoize to prevent recreating on every render
  const navigationState = useMemo(() => {
    const state = location.state || {};
    // If navigation state is empty, try to get from session storage
    if (Object.keys(state).length === 0) {
      const sessionState = getSavedStateFromSession();
      if (sessionState) {
        return sessionState;
      }
    }
    
    return state;
  }, [location.state]);

  const initialDateRange = useMemo(() => {
    const converted = convertToDateRange(navigationState.dateRange);
    return converted;
  }, [navigationState.dateRange]);

  const initialColumnFilters = useMemo(
    () => navigationState.columnFilters || {},
    [navigationState.columnFilters]
  );
  const initialPagination = useMemo(
    () => navigationState.pagination || { current: 1, pageSize: 10, total: 0 },
    [navigationState.pagination]
  );

  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [apiError, setApiError] = useState(null);
  const [tableConfig, setTableConfig] = useState(null);
  const initialFetchDone = useRef(false);

  // Redirect if no status is provided
  React.useEffect(() => {
    if (!status) {
      navigate("/prs-analytics");
    }
  }, [status, navigate]);

  // Handle authentication error
  const handleAuthError = async () => {
    setAuthError(true);
    message.error(
      "Authentication failed. Please check your token or login again."
    );
  };

  const areDateRangesEqual = (range1, range2) => {
    if (!range1 || !range2) return false;
    if (!Array.isArray(range1) || !Array.isArray(range2)) return false;
    if (range1.length !== 2 || range2.length !== 2) return false;

    return (
      range1[0] &&
      range2[0] &&
      range1[1] &&
      range2[1] &&
      range1[0].format("YYYY-MM-DD") === range2[0].format("YYYY-MM-DD") &&
      range1[1].format("YYYY-MM-DD") === range2[1].format("YYYY-MM-DD")
    );
  };

  // Handle data fetching
  const handleDataFetch = useCallback(
    async (params) => {
      try {
        setLoading(true);
        setApiError(null);

        // Always use the current dateRange state unless explicitly overridden by user filter changes
        // If params.filters.dateRange exists and is different from current state, update the state
        let currentDateRange = dateRange;

        if (
          params?.filters?.dateRange &&
          Array.isArray(params.filters.dateRange)
        ) {
          // Check if the passed date range is different from current state
          const newRange = [
            dayjs(params.filters.dateRange[0]),
            dayjs(params.filters.dateRange[1]),
          ];

          if (!areDateRangesEqual(dateRange, newRange)) {
            // User has changed the date range in the filter, update state
            setDateRange(newRange);
            currentDateRange = newRange;
          }
        }

        // Format the parameters as required by the API - always use currentDateRange
        if (
          !currentDateRange ||
          !Array.isArray(currentDateRange) ||
          currentDateRange.length !== 2
        ) {
          console.error("Invalid date range:", currentDateRange);
          message.error("Invalid date range provided");
          return { data: [], total: 0 };
        }

        const startTime = currentDateRange[0].startOf("day").toISOString();
        const endTime = currentDateRange[1].endOf("day").toISOString();

        // Build the query parameters in the format expected by the API
        const requestParams = {
          startTime,
          endTime,
          type: ["ECOMM"],
          status: status || "PRS_PENDING",
          pageSize: String(params?.pagination?.pageSize || 10),
          pageNumber: String(params?.pagination?.pageNumber || 1),
        };

        // Add sorting if present
        if (params?.sort?.field) {
          requestParams.sortField = params.sort.field;
          requestParams.sortOrder =
            params.sort.order === "ascend" ? "asc" : "desc";
        }

        // Add any additional filters
        if (params?.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (value && key !== "dateRange" && key !== "status") {
              requestParams[key] = String(value);
            }
          });
        }

        const searchParams = new URLSearchParams();
        Object.entries(requestParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((val) => searchParams.append(key, String(val)));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });

        const apiUrl = `${GET_PRS_STATUS_DATA}?${searchParams.toString()}`;

        const response = await API.Api(apiUrl).getCustomHeader({
          kid: "1",
          authtype: "jwt",
        });

        if (response.status === 403) {
          await handleAuthError();
          return { data: [], total: 0 };
        }

        if (!response?.response?.data) {
          throw new Error("Invalid API response format");
        }

        const result = response.response.data;

        if (result.status === 200) {
          // Update table configuration with API response metadata
          if (result) {
            const newConfig = {
              ...result,
              pagination: {
                ...result.pagination,
                current: parseInt(requestParams.pageNumber),
                pageSize: parseInt(requestParams.pageSize),
                total: result.pagination.totalCount,
              },
            };
            setTableConfig(newConfig);
          }

          // Ensure data is properly formatted with unique keys
          const dataWithKeys = Array.isArray(result.data)
            ? result.data.map((item) => ({
              ...item,
              key: item.prsNumber || item.id,
            }))
            : [];

          setData(dataWithKeys);
          setTotal(result.pagination?.totalCount || 0);

          return {
            data: dataWithKeys,
            total: result.pagination?.totalCount || 0,
            pagination: {
              current: parseInt(requestParams.pageNumber),
              pageSize: parseInt(requestParams.pageSize),
              total: result.pagination?.totalCount || 0,
            },
          };
        } else {
          throw new Error(result.message || "Failed to fetch data");
        }
      } catch (error) {
        console.error("Error in handleDataFetch:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "Error fetching PRS data";
        setApiError({
          type: "error",
          title: "Error",
          messages: [errorMessage],
          statusCode: error?.status || 500,
        });
        message.error(`Error fetching data: ${errorMessage}`);
        return { data: [], total: 0 };
      } finally {
        setLoading(false);
      }
    },
    [dateRange, setDateRange, status]
  );

  // Initial data fetch with date range from navigation state or default
  useEffect(() => {
    if (!initialFetchDone.current && status) {
      initialFetchDone.current = true;
      handleDataFetch({
        pagination: {
          pageNumber: initialPagination.current || 1,
          pageSize: initialPagination.pageSize || 10,
        },
        filters: {
          dateRange: initialDateRange,
          status,
          ...initialColumnFilters,
        },
      });
    }
  }, [
    status,
    handleDataFetch,
    initialDateRange,
    initialPagination,
    initialColumnFilters,
  ]);

  // Handle export functionality
  const handleExport = async (params) => {
    try {
      setLoading(true);
      setApiError(null);

      const exportParams = {
        startTime: dateRange[0].startOf("day").toISOString(),
        endTime: dateRange[1].endOf("day").toISOString(),
        type: ["ECOMM"],
        downloadCsv: "1",
        status: status || "",
      };

      // Add any additional filters from params
      if (params?.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value && key !== "dateRange" && key !== "status") {
            exportParams[key] = String(value);
          }
        });
      }

      const searchParams = new URLSearchParams();
      Object.entries(exportParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => searchParams.append(key, String(val)));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });

      const apiUrl = `${GET_PRS_STATUS_DATA}?${searchParams.toString()}`;

      const result = await API.Api(apiUrl).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (result?.response?.status === 200 && result?.response?.data) {
        const url = window.URL.createObjectURL(
          new Blob([result.response.data])
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `PRS_${status}_${dayjs().format("DD-MM-YYYY")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success("Export successful");
      } else {
        throw new Error("Failed to export data");
      }
    } catch (error) {
      const errorMessage = error?.message || "Error exporting data";
      message.error(`Export failed: ${errorMessage}`);
      setApiError({
        type: "error",
        title: "Export Error",
        messages: [errorMessage],
        statusCode: error?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderError = () => {
    if (!apiError && !authError) return null;

    const errorMessages = {
      type: "error",
      title: "Authentication Error",
      messages: [
        "Authentication failed. Please check your token or login again.",
      ],
      statusCode: 403,
    };
    const error = authError ? errorMessages : apiError;

    return (
      <Alert
        message={error.title}
        description={
          <div>
            {error.statusCode && (
              <div style={{ marginBottom: "8px" }}>
                Status Code: {error.statusCode}
              </div>
            )}
            {error.messages.map((msg, index) => (
              <div
                key={index}
                style={{
                  color: error.type === "error" ? "#ff4d4f" : "#faad14",
                }}
              >
                • {msg}
              </div>
            ))}
          </div>
        }
        type={error.type === "validation" ? "warning" : "error"}
        showIcon
        style={{ marginBottom: "16px" }}
        closable
        onClose={() => {
          if (authError) {
            setAuthError(false);
          } else {
            setApiError(null);
          }
        }}
      />
    );
  };

  // If no status is provided, show loading until redirect happens
  if (!status) {
    return <Spin />;
  }

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <div style={{ marginBottom: "16px" }}>
        <Heading title={"Back"} navigateTo="/prs-analytics" />
      </div>

      <Card
        style={{
          padding: "24px",
          backgroundColor: "#fff",
          border: "none",
          borderRadius: "8px",
        }}
      >
        {renderError()}
        {tableConfig && (
          <div className="prs-status-details">
            <DynamicTable
              key={status}
              config={tableConfig}
              onDataFetch={handleDataFetch}
              onExport={handleExport}
              loading={loading}
              defaultFilters={{
                dateRange: {
                  defaultValue: initialDateRange,
                },
              }}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default PRSStatusDetails;
